'use client';

import { useTranslations } from 'next-intl';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  CVSS_31_MODIFIED_BASE_METRIC_KEYS,
  CVSS_40_MODIFIED_BASE_METRIC_KEYS,
  CVSS_SECURITY_REQUIREMENT_METRIC_KEYS,
  calculateFindingCvssDisplayScore,
  environmentalRequirementsForVersion,
  getSeverityFromScore,
  isEnvironmentalRequirementsConfigured,
  stripEnvironmentalMetricsFromVector,
  type CvssEnvironmentalRequirements,
  type CvssModifiedBaseMetrics,
  type CvssSecurityRequirementValue,
  type FindingCvssVersionKey,
} from '@terra/shared-cvss';
import { ArrowRight01Icon, SlidersHorizontalIcon } from '@terra/ui-icons';
import { Badge } from '../../components/badge';
import { BadgeSeverity } from '../../components/styled/badge-severity';
import {
  BodyDefault,
  BodyStrong,
  HeadingSection,
  HeadingItem,
  LabelDefault,
  MetaDefault,
  MonoSmall,
} from '../../components/typography';
import type { CvssMetricTone } from './finding-cvss-tab-metrics';
import { CVSS_TONE_STYLES } from './finding-cvss-tone-styles';

type ModifiedMetricKey = keyof CvssModifiedBaseMetrics;

const REQUIREMENT_TONES: Record<
  CvssSecurityRequirementValue,
  CvssMetricTone | 'unknown'
> = {
  H: 'raises',
  M: 'some',
  L: 'lowers',
  X: 'unknown',
};

const MODIFIED_VALUE_FAMILIES = {
  mav: 'mav',
  mac: 'mac',
  mat: 'mat',
  mpr: 'mpr',
  mui: 'mui',
  ms: 'ms',
  mc: 'impact',
  mi: 'impact',
  ma: 'impact',
  mvc: 'impact',
  mvi: 'impact',
  mva: 'impact',
  msc: 'impact',
  msi: 'subsequentImpact',
  msa: 'subsequentImpact',
} as const satisfies Record<ModifiedMetricKey, string>;

const MODIFIED_KEYS_BY_VERSION: Record<
  FindingCvssVersionKey,
  readonly ModifiedMetricKey[]
> = {
  '4.0': CVSS_40_MODIFIED_BASE_METRIC_KEYS,
  '3.1': CVSS_31_MODIFIED_BASE_METRIC_KEYS,
};

export type FindingCvssEnvironmentalPanelProps = {
  version: FindingCvssVersionKey;
  vector: string | null;
  requirements?: Partial<CvssEnvironmentalRequirements> | null;
};

function ScoreValue({ label, score }: { label: string; score: number }) {
  const severity = getSeverityFromScore(score);

  return (
    <VStack gap={1} alignItems="flex-start">
      <MetaDefault color="fg.muted">{label}</MetaDefault>
      <HStack gap={2}>
        <HeadingItem>{score.toFixed(1)}</HeadingItem>
        <BadgeSeverity severity={severity} size="sm">
          {severity}
        </BadgeSeverity>
      </HStack>
    </VStack>
  );
}

export function FindingCvssEnvironmentalPanel({
  version,
  vector,
  requirements,
}: FindingCvssEnvironmentalPanelProps) {
  const t = useTranslations('designSystem.findings.cvssVersions');
  const tSettings = useTranslations('designSystem.settings.cvssScoring');
  const resolved = environmentalRequirementsForVersion({
    requirements,
    version,
  });
  const isConfigured = isEnvironmentalRequirementsConfigured(resolved);

  return (
    <VStack
      data-testid={`cvss-environmental-panel-${version}`}
      gap={3}
      alignItems="stretch"
      w="full"
    >
      <VStack gap={1} alignItems="flex-start">
        <HeadingSection>
          {tSettings('environmentalRequirements')}
        </HeadingSection>
        <MetaDefault color="fg.muted">
          {t('environmentalDescription')}
        </MetaDefault>
      </VStack>
      {isConfigured ? (
        <FindingCvssEnvironmentalConfigured
          version={version}
          vector={vector}
          requirements={resolved}
        />
      ) : (
        <HStack
          data-testid="cvss-environmental-empty"
          gap={4}
          alignItems="flex-start"
          p={4}
          borderRadius="lg"
          borderWidth="1px"
          borderStyle="dashed"
          borderColor="border.default"
        >
          <Box
            p={2}
            borderRadius="md"
            bg="bg.subtle"
            color="fg.muted"
            flexShrink={0}
          >
            <SlidersHorizontalIcon size={20} />
          </Box>
          <VStack gap={1} alignItems="flex-start" minW={0}>
            <BodyStrong>{t('environmentalEmptyTitle')}</BodyStrong>
            <BodyDefault color="fg.muted">
              {t('environmentalEmptyBody')}
            </BodyDefault>
            <MonoSmall color="fg.muted">
              {t('environmentalSettingsPath')}
            </MonoSmall>
          </VStack>
        </HStack>
      )}
    </VStack>
  );
}

function FindingCvssEnvironmentalConfigured({
  version,
  vector,
  requirements,
}: {
  version: FindingCvssVersionKey;
  vector: string | null;
  requirements: CvssEnvironmentalRequirements;
}) {
  const t = useTranslations('designSystem.findings.cvssVersions');
  const tSettings = useTranslations('designSystem.settings.cvssScoring');
  const displayed = vector
    ? calculateFindingCvssDisplayScore({
        vector: stripEnvironmentalMetricsFromVector(vector),
        environmentalRequirements: requirements,
      })
    : null;
  const modifiedKeys = MODIFIED_KEYS_BY_VERSION[version].filter(
    (key) => requirements[key] !== 'X',
  );

  return (
    <VStack gap={4} alignItems="stretch">
      <Box
        display="grid"
        gap={2}
        gridTemplateColumns="repeat(auto-fit, minmax(min(160px, 100%), 1fr))"
      >
        {CVSS_SECURITY_REQUIREMENT_METRIC_KEYS.map((key) => {
          const value = requirements[key];
          const styles = CVSS_TONE_STYLES[REQUIREMENT_TONES[value]];

          return (
            <VStack
              key={key}
              data-testid={`cvss-environmental-${key}`}
              gap={1}
              alignItems="flex-start"
              p={3}
              minW={0}
              overflowWrap="anywhere"
              borderRadius="md"
              borderWidth="1px"
              style={{ backgroundColor: styles.bg, borderColor: styles.border }}
            >
              <LabelDefault color="fg.muted">{tSettings(key)}</LabelDefault>
              <BodyStrong style={{ color: styles.fg }}>
                {tSettings(`requirements.${value}`)}
              </BodyStrong>
            </VStack>
          );
        })}
      </Box>
      {modifiedKeys.length > 0 ? (
        <VStack gap={2} alignItems="flex-start">
          <LabelDefault color="fg.muted">
            {tSettings('additionalModifiers')}
          </LabelDefault>
          <HStack gap={2} flexWrap="wrap">
            {modifiedKeys.map((key) => (
              <Badge key={key} variant="emphasis" size="md">
                {`${tSettings(`modified.${key}`)}: ${tSettings(
                  `metricValues.${MODIFIED_VALUE_FAMILIES[key]}.${requirements[key]}`,
                )}`}
              </Badge>
            ))}
          </HStack>
        </VStack>
      ) : null}
      {displayed ? (
        <HStack
          data-testid="cvss-environmental-score"
          gap={4}
          flexWrap="wrap"
          alignItems="center"
          p={4}
          borderRadius="md"
          bg="bg.subtle"
        >
          <ScoreValue label={t('baseScore')} score={displayed.baseScore} />
          <Box color="fg.muted">
            <ArrowRight01Icon size={20} />
          </Box>
          <ScoreValue
            label={t('environmentalScore')}
            score={displayed.displayedScore}
          />
          <MetaDefault color="fg.muted" flex="1 1 200px">
            {t('environmentalScoreNote', { version })}
          </MetaDefault>
        </HStack>
      ) : null}
    </VStack>
  );
}
